// Pentest Scope
// -------------
// Wizard step (Add-Application only) that collects the optional API domains and
// shows the user the resulting pentest scope live as they type. The scope
// preview is FE-only and display-only — it never talks to the backend, which
// remains the sole authority on what is in scope. See [pentestScope.ts].
//
// API domains are create-once and never edited after the app is created, so
// this step is the single place the user declares them. The existing-app
// Settings flow surfaces them read-only elsewhere and never renders this step.

import { useCallback, useMemo, useState } from "react";
import {
  BUTTON_NEXT,
  BUTTON_PREV,
  EMPTY_STRING,
  MAX_LENGTH_API_DOMAINS,
} from "@/constants/definitions";
import WebApplicationClass from "@/models/webApplication";
import { Input } from "@/components/ui/input";
import { FormField } from "@/components/ui/form-field";
import Explainer from "@/components/common/Explainer";
import { buildPentestScopePreview } from "@/utils/pentestScope";

const APP_API_DOMAINS_INPUT_NAME = "appApiDomains";
// API domains are host + optional base path (e.g. "api.example.com/rest"), or
// full URLs, so the split must NOT break on ":" or "/" (scheme, port, path).
// Commas are the documented separator; whitespace and semicolons are tolerated.
// Validation is deliberately light: the backend canonicalises each entry to
// host + base path and drops anything with no host.
const API_DOMAINS_WHITELIST_CHARS = /^[\sA-Za-z0-9\-._:/,;]+$/;
const API_DOMAINS_SPLIT_CHARS = /[\s,;]+/;
const ERROR_INVALID_CHARS_API_DOMAINS = "Invalid characters in the API domains list";

interface PentestScopeProps {
  webapp: WebApplicationClass;
  // name is BUTTON_NEXT or BUTTON_PREV so the wizard knows which way to move.
  onComplete: (name: string, webapp: WebApplicationClass) => void;
  onCancel?: () => void;
}

function PentestScope({ webapp, onComplete, onCancel }: PentestScopeProps) {
  // Comma-joined for display; parsed back to a list on navigation. Handles both
  // the fresh-wizard default (EMPTY_STRING) and a re-entry after Next (a list).
  const [apiDomains, setApiDomains] = useState<string>(
    Array.isArray(webapp.apiDomains)
      ? webapp.apiDomains.join(", ")
      : webapp.apiDomains || EMPTY_STRING,
  );
  const [errormessage, setErrormessage] = useState<string>(EMPTY_STRING);

  // Live, FE-only scope preview. Recomputed as the user types; the app URL was
  // fixed on the previous step so its scope is constant here.
  const scopePreview = useMemo(
    () => buildPentestScopePreview(webapp.url, apiDomains),
    [webapp.url, apiDomains],
  );

  // Comma/space/semicolon-separated free text -> list of trimmed, non-empty
  // entries. Normalization to host + base path is deferred to the backend.
  const buildApiDomainList = useCallback((domainsInput: string): string[] => {
    return domainsInput
      .split(API_DOMAINS_SPLIT_CHARS)
      .map((domainEntry) => domainEntry.trim())
      .filter((domainEntry) => domainEntry.length > 0);
  }, []);

  const onApiDomainsChange = useCallback((event: React.ChangeEvent<HTMLInputElement>): void => {
    const { value } = event.target;
    setApiDomains(value);
    if (value && !API_DOMAINS_WHITELIST_CHARS.test(value)) {
      setErrormessage(ERROR_INVALID_CHARS_API_DOMAINS);
    } else {
      setErrormessage(EMPTY_STRING);
    }
  }, []);

  const onButtonClick = useCallback((event: React.MouseEvent<HTMLButtonElement>): void => {
    const name = event.currentTarget.name;

    // Only block the forward step on bad input; going Back to fix it must
    // always be allowed. API domains are optional, so an empty value is fine.
    if (BUTTON_NEXT === name && apiDomains && !API_DOMAINS_WHITELIST_CHARS.test(apiDomains)) {
      setErrormessage(ERROR_INVALID_CHARS_API_DOMAINS);
      return;
    }

    // Persist as a list (matching the backend / DeepDiscovery contract) so the
    // entries survive back-and-forth navigation. The backend write itself is
    // deferred to the Finish step, once the webapp ID exists.
    webapp.apiDomains = buildApiDomainList(apiDomains);
    onComplete(name, webapp);
  }, [apiDomains, webapp, onComplete, buildApiDomainList]);

  const webAppScope = scopePreview.webApp;

  return (
    <div className="flex flex-col gap-5">
      {errormessage && <p className="text-sm text-red-600">{errormessage}</p>}

      {/* What this step is */}
      <p className="text-sm text-gray-500">
        Testing is limited to the scope shown below. Your application URL is always in scope. If your
        app calls APIs on a different host or subdomain (e.g. app at <code>app.example.com</code>,
        API at <code>api.example-backend.com/rest</code>), add those hosts so they&rsquo;re tested too.
        You can enter a single domain or a comma-separated list. Each API domain is host-exact and
        scoped to the base path you give, and you&rsquo;ll be asked to verify ownership of each in a
        later step. API domains are set once and can&rsquo;t be changed after the app is created.
      </p>

      {/* Application URL scope — read-only, derived from the previous step */}
      <div className="rounded-lg border border-gray-100 bg-gray-50 px-4 py-3">
        <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1">
          Application URL scope
        </p>
        {webAppScope ? (
          <>
            <p className="text-sm font-medium text-gray-900">{webAppScope.host}</p>
            <p className="text-xs text-gray-500 mt-0.5">{webAppScope.description}</p>
          </>
        ) : (
          <p className="text-xs text-gray-400">
            Enter a valid Application URL on the previous step to see its scope.
          </p>
        )}
      </div>

      {/* General mental model behind the per-URL preview above — why a
          single subdomain covers only itself but a root domain covers all. */}
      <Explainer label="how scope is decided" className="-mt-2">
        <div className="flex flex-col gap-2">
          <p>Scope is derived from your Application URL:</p>
          <ul className="flex list-disc flex-col gap-1.5 pl-4">
            <li>
              <span className="font-medium text-gray-700">A specific subdomain</span>{" "}
              (e.g. <code>app.example.com</code>) — only that exact host is tested.
              Sibling subdomains like <code>api.example.com</code> or{" "}
              <code>admin.example.com</code> are <em>not</em> tested unless you add
              them as API domains below.
            </li>
            <li>
              <span className="font-medium text-gray-700">A root domain</span> (e.g.{" "}
              <code>example.com</code>, or a <code>www.</code> host) — every subdomain
              of <code>example.com</code> is in scope.
            </li>
            <li>
              <span className="font-medium text-gray-700">An IP address or internal host</span>{" "}
              — only that exact host is tested.
            </li>
          </ul>
        </div>
      </Explainer>

      {/* API domains input */}
      <FormField
        label="Application API Domains"
        htmlFor="appApiDomains"
        helperText="Optional. Enter each as a host with an optional base path; separate multiple with commas."
        error={errormessage || null}
      >
        <Input
          type="text"
          name={APP_API_DOMAINS_INPUT_NAME}
          id="appApiDomains"
          value={apiDomains}
          placeholder="e.g. api.example-backend.com/rest, payments.example.com"
          maxLength={MAX_LENGTH_API_DOMAINS}
          hasError={Boolean(errormessage)}
          onChange={onApiDomainsChange}
        />
      </FormField>

      {/* Live API-domain scope preview */}
      {scopePreview.apiDomains.length > 0 && (
        <div className="flex flex-col gap-2">
          <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">
            API domain scope
          </p>
          <ul className="flex flex-col gap-2">
            {scopePreview.apiDomains.map((apiDomainScope) => (
              <li
                key={apiDomainScope.label}
                className="rounded-lg border border-gray-100 px-4 py-3"
              >
                <p className="text-sm font-medium text-gray-900">{apiDomainScope.label}</p>
                <p className="text-xs text-gray-500 mt-0.5">{apiDomainScope.description}</p>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className={`flex items-center pt-4 border-t border-gray-100 ${onCancel ? "justify-between" : "justify-end"}`}>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex items-center justify-center rounded-md border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 cursor-pointer"
          >
            Cancel
          </button>
        )}
        <div className="flex items-center gap-2">
          <button
            type="button"
            name={BUTTON_PREV}
            onClick={onButtonClick}
            className="inline-flex items-center justify-center rounded-md border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 cursor-pointer"
          >
            Previous
          </button>
          <button
            type="button"
            name={BUTTON_NEXT}
            onClick={onButtonClick}
            className="inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 cursor-pointer"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}

export default PentestScope;
